/** Settings → Follow-ups and referral (P4-04, P4-05): off by default, written by the agency. */

import {
  type FollowUpSettings,
  type ReferralProgram,
  type RewardEnum,
  useCrmFollowUpSettingsList,
  useCrmFollowUpSettingsPartialUpdate,
  useCrmReferralProgramList,
  useCrmReferralProgramPartialUpdate,
  useCrmReferralsList,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, SelectField, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { fieldErrors, useCanEditSettings } from "../lib/settings";

type Texts = Record<string, Record<string, string>>;

function useRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/crm/") });
}

function FollowUps({ row }: { row: FollowUpSettings }) {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const save = useCrmFollowUpSettingsPartialUpdate({ mutation: { onSuccess: useRefresh() } });
  const [enabled, setEnabled] = useState(Boolean(row.enabled));
  const [delays, setDelays] = useState(((row.delays_days as number[]) ?? []).join(", "));
  const [messages, setMessages] = useState<Texts>((row.messages as Texts) ?? {});
  const [templates, setTemplates] = useState<Record<string, string>>((row.templates as Record<string, string>) ?? {});
  const [stops, setStops] = useState(((row.stop_words as string[]) ?? []).join(", "));
  const steps = delays
    .split(",")
    .map((d) => Number.parseInt(d.trim(), 10))
    .filter((d) => Number.isInteger(d) && d > 0);
  const errors = fieldErrors(save.error);
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("growth.followUps")}</h2>
          <p className="muted">{t("growth.followUpsIntro")}</p>
        </div>
        <Badge tone={enabled ? "success" : "default"}>{enabled ? t("growth.on") : t("growth.off")}</Badge>
      </div>
      <fieldset className="plain-fieldset stack" disabled={!canEdit}>
        <SoftToggle label={t("growth.enable")} checked={enabled} onChange={setEnabled} />
        <TextField label={t("growth.delays")} hint={t("growth.delaysHint")} value={delays} error={errors.delays_days} onChange={(e) => setDelays(e.target.value)} />
        {steps.map((step) => (
          <div key={step} className="installment-row stack">
            <h3>{t("growth.step", { day: step })}</h3>
            <div className="form-grid">
              <TextAreaField
                label={t("growth.textFr")}
                rows={2}
                value={messages[String(step)]?.fr ?? ""}
                onChange={(e) => setMessages((m) => ({ ...m, [String(step)]: { ...(m[String(step)] ?? {}), fr: e.target.value } }))}
              />
              <TextAreaField
                label={t("growth.textEn")}
                rows={2}
                value={messages[String(step)]?.en ?? ""}
                onChange={(e) => setMessages((m) => ({ ...m, [String(step)]: { ...(m[String(step)] ?? {}), en: e.target.value } }))}
              />
              <TextField
                label={t("growth.template")}
                hint={t("growth.templateHint")}
                value={templates[String(step)] ?? ""}
                onChange={(e) => setTemplates((m) => ({ ...m, [String(step)]: e.target.value }))}
              />
            </div>
          </div>
        ))}
        <TextField label={t("growth.stopWords")} hint={t("growth.stopHint")} value={stops} onChange={(e) => setStops(e.target.value)} />
        <div className="row">
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                id: row.id,
                data: {
                  enabled,
                  delays_days: steps,
                  messages,
                  templates,
                  stop_words: stops.split(",").map((w) => w.trim()).filter(Boolean),
                },
              })
            }
          >
            {t("common.save")}
          </Button>
          {save.isSuccess ? <span role="status" className="ok">{t("common.saved")}</span> : null}
        </div>
      </fieldset>
    </GlassCard>
  );
}

function Referral({ row }: { row: ReferralProgram }) {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const save = useCrmReferralProgramPartialUpdate({ mutation: { onSuccess: useRefresh() } });
  const referrals = useCrmReferralsList();
  const [enabled, setEnabled] = useState(Boolean(row.enabled));
  const [prefix, setPrefix] = useState(row.code_prefix ?? "");
  const [reward, setReward] = useState<RewardEnum>(row.reward ?? "commission");
  const [amount, setAmount] = useState(row.amount_xaf === null || row.amount_xaf === undefined ? "" : String(row.amount_xaf));
  const errors = fieldErrors(save.error);
  const list = referrals.data?.status === 200 ? referrals.data.data.results : [];
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("growth.referral")}</h2>
          <p className="muted">{t("growth.referralIntro")}</p>
        </div>
        <Badge tone={enabled ? "success" : "default"}>{enabled ? t("growth.on") : t("growth.off")}</Badge>
      </div>
      <fieldset className="plain-fieldset stack" disabled={!canEdit}>
        <SoftToggle label={t("growth.enableReferral")} checked={enabled} onChange={setEnabled} />
        <div className="form-grid">
          <TextField label={t("growth.prefix")} value={prefix} error={errors.code_prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} />
          <SelectField
            label={t("growth.reward")}
            value={reward}
            options={(["commission", "discount"] as const).map((r) => ({ value: r, label: t(`growth.rewards.${r}`) }))}
            onChange={(e) => setReward(e.target.value as RewardEnum)}
          />
          <TextField label={t("growth.amount")} hint={t("growth.amountHint")} inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} />
        </div>
        <div className="row">
          <Button
            disabled={save.isPending}
            onClick={() => save.mutate({ id: row.id, data: { enabled, code_prefix: prefix, reward, amount_xaf: amount ? Number(amount) : null } })}
          >
            {t("common.save")}
          </Button>
          {save.isSuccess ? <span role="status" className="ok">{t("common.saved")}</span> : null}
        </div>
      </fieldset>
      <h3>{t("growth.earned")}</h3>
      {list.length ? (
        <div className="table-wrap">
        <table className="table">
          <tbody>
            {list.map((r) => (
              <tr key={r.id}>
                <td>{r.referrer_name}</td>
                <td className="muted">→ {r.client_name}</td>
                <td>{t(`growth.rewards.${r.reward}`)}</td>
                <td>{r.amount_xaf === null || r.amount_xaf === undefined ? "—" : formatXaf(r.amount_xaf)}</td>
                <td>
                  <Badge>{t(`growth.status.${r.status ?? "earned"}`)}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <p className="muted">{t("growth.noReferral")}</p>
      )}
    </GlassCard>
  );
}

export function GrowthPage() {
  const { t } = useTranslation();
  const followUps = useCrmFollowUpSettingsList();
  const program = useCrmReferralProgramList();
  const follow = followUps.data?.status === 200 ? followUps.data.data.results[0] : undefined;
  const referral = program.data?.status === 200 ? program.data.data.results[0] : undefined;
  return (
    <SettingsLayout title={t("growth.title")} intro={t("growth.intro")}>
      <div className="grid">
        {follow ? <FollowUps key={follow.id} row={follow} /> : <Skeleton style={{ height: 200 }} />}
        {referral ? <Referral key={referral.id} row={referral} /> : <Skeleton style={{ height: 200 }} />}
      </div>
    </SettingsLayout>
  );
}
